import { useExportDashboardRFS } from "@/api/dashboard";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { getExceedDuration } from "@/utils/helper/getExceedDuration";
import { axiosInstance } from "@/utils/lib/axios";
import { swalErrorDefault } from "@/utils/lib/swal";
import { useEffect, useState } from "react";

interface UseRFSDownloadProps {
  selectedPICLinknets: string;
  selectedPICVendors: string;
  selectedTenantNames: string;
  selectedVendorNames: string;
  selectedDateMonthYear: string;
  isVendor: boolean;
  companyCode?: string;
  userId?: string;
}

export const useRFSDownload = ({
  selectedPICLinknets,
  selectedPICVendors,
  selectedTenantNames,
  selectedVendorNames,
  selectedDateMonthYear,
  isVendor,
  companyCode,
  userId,
}: UseRFSDownloadProps) => {
  const [status, setStatus] = useState<string>("IDLE");
  const [isProcessing, setIsProcessing] = useState(false);
  const { mutate: exportList, isPending: isExporting } =
    useExportDashboardRFS();

  const downloadTimestamp = localStorage.getItem("isDownloadDashboardRFS");
  const isDownloadApd = Boolean(
    downloadTimestamp && downloadTimestamp.trim().length > 0,
  );

  const createParams = (): DataConfig => ({
    "picLinknet.ids": selectedPICLinknets ?? undefined,
    "picVendor.ids": selectedPICVendors ?? undefined,
    "trackerdataApd.tenantName": selectedTenantNames ?? undefined,
    "trackerdataApd.vendor":
      (isVendor ? companyCode : selectedVendorNames) ?? undefined,
    dateMonthYear: selectedDateMonthYear,
    entityId: userId,
  });

  const handleExport = () => {
    const params = createParams();

    exportList(
      {
        config: params,
        isDownload: isDownloadApd,
        onDownload: !!isDownloadApd,
      },
      {
        onSuccess: (response) => {
          setStatus(response.status);
        },
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };

  const checkAndHandleAutoExport = async () => {
    if (!isDownloadApd || isProcessing) return;

    setIsProcessing(true);
    try {
      const response = await axiosInstance.get(
        "/api/crud/master-param?paramType=ITV_ASYNC_IDLE&active=true",
      );

      const exceedDuration = getExceedDuration(
        downloadTimestamp ?? "",
        response,
      );

      if (exceedDuration) {
        localStorage.removeItem("isDownloadDashboardRFS");
        setStatus("IDLE");
      } else {
        const params = createParams();
        exportList(
          {
            config: params,
            isDownload: isDownloadApd,
            onDownload: false,
          },
          {
            onSuccess: (response) => {
              setStatus(response.status);
            },
            onError: () => {
              swalErrorDefault.fire();
            },
          },
        );
      }
    } catch (error) {
      console.error("Error fetching duration:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    checkAndHandleAutoExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportList, isProcessing]);

  return {
    status,
    isExporting,
    handleExport,
  };
};
